import { ALERT_RULE_TYPES, ALERT_TYPE_CATALOG, AlertSeverity, ROLE_PRESETS, SYSTEM_ROLE_CODES, type AlertRuleType } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowRight, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, Dialog, EmptyState, Field, PageHeader, Skeleton, Spinner, Table, Td, Th, useConfirm, useToast } from '../../components/ui';
import { api, type Paginated } from '../../lib/api';
import type { Group, VehicleRow } from '../vehicles/VehiclesPage';
import { severityTone, type RuleRow } from './types';

interface PlaceLite { id: string; name: string }
interface StaffUser { id: string; fullName: string }
const GROUPS = ['movement', 'geofence', 'connectivity', 'fleet', 'trips', 'finance'] as const;

export function AlertRulesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<{ open: boolean; editing?: RuleRow | null }>({ open: false });
  const q = useQuery({ queryKey: ['alert-rules'], queryFn: () => api<RuleRow[]>('/alert-rules') });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['alert-rules'] });
  const toggle = useMutation({ mutationFn: (id: string) => api(`/alert-rules/${id}/toggle`, { method: 'POST' }), onSuccess: invalidate, onError: toast.error });
  const remove = useMutation({ mutationFn: (id: string) => api(`/alert-rules/${id}`, { method: 'DELETE' }), onSuccess: () => { toast.success(t('common.saved')); invalidate(); }, onError: toast.error });
  const grouped = useMemo(() => GROUPS.map((g) => ({ group: g, rules: (q.data ?? []).filter((r) => ALERT_TYPE_CATALOG[r.type]?.group === g) })).filter((g) => g.rules.length), [q.data]);

  return (
    <div className="flex flex-col gap-4">
      <Link to="/alerts" className="flex items-center gap-1 text-sm text-text-muted hover:text-text"><ArrowRight size={14} className="ltr:rotate-180" /> {t('nav.alerts')}</Link>
      <PageHeader title={t('alerts.rules')} subtitle={t('alerts.rulesHint')} actions={<Can perm="alertrules:create"><button className="btn-primary" onClick={() => setDialog({ open: true })}><Plus size={16} /> {t('alerts.addRule')}</button></Can>} />
      {q.isLoading ? <Skeleton className="h-72" /> : !q.data?.length ? <div className="card"><EmptyState title={t('alerts.noRules')} /></div> : grouped.map((g) => (
        <section key={g.group} className="flex flex-col gap-2">
          <h2 className="text-sm font-bold text-text-2">{t(`alerts.groups.${g.group}`)}</h2>
          <Table>
            <thead><tr><Th>{t('alerts.ruleName')}</Th><Th>{t('alerts.type')}</Th><Th>{t('alerts.severity')}</Th><Th>{t('alerts.scope')}</Th><Th>{t('alerts.cooldown')}</Th><Th>{t('alerts.last7d')}</Th><Th>{t('alerts.enabled')}</Th><Th className="w-1" sticky="end" /></tr></thead>
            <tbody>
              {g.rules.map((r) => (
                <tr key={r.id} className={clsx('group hover:bg-surface-hover', !r.isEnabled && 'opacity-60')}>
                  <Td><div className="font-semibold">{r.name}</div>{r.schedule ? <div className="text-xs text-text-faint">{t('alerts.scheduled')} {r.schedule.from}–{r.schedule.to}</div> : null}</Td>
                  <Td className="text-xs text-text-muted">{t(`alerts.types.${r.type}`)}{Object.keys(r.config).length ? <div dir="ltr" className="text-text-faint">{Object.entries(r.config).map(([k, v]) => `${k}=${v}`).join(' ')}</div> : null}</Td>
                  <Td><Badge tone={severityTone(r.severity)} dot>{t(`alerts.severities.${r.severity}`)}</Badge></Td>
                  <Td className="text-xs">{r.allVehicles ? t('alerts.allVehicles') : t('alerts.scopeCount', { vehicles: r.vehicleIds.length, groups: r.groupIds.length })}{r.placeIds.length ? <div className="text-text-faint">{t('alerts.placesCount', { count: r.placeIds.length })}</div> : null}</Td>
                  <Td className="tabular-nums text-xs">{r.cooldownSec ? `${Math.round(r.cooldownSec / 60)} ${t('units.min')}` : '—'}</Td>
                  <Td className="tabular-nums">{r.eventsLast7d}</Td>
                  <Td><Can perm="alertrules:update"><button className={clsx('btn-ghost size-8 !p-0', r.isEnabled ? 'text-success-fg' : 'text-text-faint')} onClick={() => toggle.mutate(r.id)} aria-label={r.isEnabled ? t('alerts.disable') : t('alerts.enable')} title={r.isEnabled ? t('alerts.disable') : t('alerts.enable')}><Power size={16} /></button></Can></Td>
                  <Td className="text-end" sticky="end"><div className="flex justify-end gap-1">
                    <Can perm="alertrules:update"><button className="btn-ghost size-8 !p-0" onClick={() => setDialog({ open: true, editing: r })} aria-label={t('common.edit')} title={t('common.edit')}><Pencil size={14} /></button></Can>
                    <Can perm="alertrules:delete"><button className="btn-ghost size-8 !p-0 text-danger-fg" onClick={() => { void confirmDialog(t('alerts.deleteRuleConfirm'), { danger: true }).then((ok) => { if (ok) remove.mutate(r.id); }); }} aria-label={t('common.delete')} title={t('common.delete')}><Trash2 size={14} /></button></Can>
                  </div></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
      ))}
      <RuleDialog open={dialog.open} editing={dialog.editing} onClose={() => setDialog({ open: false })} />
    </div>
  );
}

/* ─────────────────────────── rule editor ─────────────────────────── */

interface Form {
  name: string;
  type: AlertRuleType;
  severity: (typeof AlertSeverity)[number];
  isEnabled: boolean;
  allVehicles: boolean;
  vehicleIds: string[];
  groupIds: string[];
  placeIds: string[];
  config: Record<string, string>;
  scheduled: boolean;
  days: number[];
  from: string;
  to: string;
  cooldownMin: string;
  recipientRoleCodes: string[];
  recipientUserIds: string[];
  notifyDriver: boolean;
  push: boolean;
}

const defaults = (type: AlertRuleType): Form => ({
  name: '',
  type,
  severity: ALERT_TYPE_CATALOG[type].defaultSeverity,
  isEnabled: true,
  allVehicles: true,
  vehicleIds: [],
  groupIds: [],
  placeIds: [],
  config: Object.fromEntries(ALERT_TYPE_CATALOG[type].config.map((c) => [c.key, String(c.default)])),
  scheduled: false,
  days: [0, 1, 2, 3, 4, 6],
  from: '08:00',
  to: '18:00',
  cooldownMin: '5',
  recipientRoleCodes: ['SYSTEM_ADMIN', 'FLEET_MANAGER', 'DISPATCHER'],
  recipientUserIds: [],
  notifyDriver: false,
  push: true,
});

function RuleDialog({ open, onClose, editing }: { open: boolean; onClose: () => void; editing?: RuleRow | null }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState<Form>(defaults('SPEEDING'));
  const vehicles = useQuery({ queryKey: ['vehicles-lite-all'], enabled: open, queryFn: () => api<Paginated<VehicleRow>>('/vehicles?pageSize=200').then((r) => r.data) });
  const groups = useQuery({ queryKey: ['vehicle-groups'], enabled: open, queryFn: () => api<Group[]>('/vehicle-groups') });
  const places = useQuery({ queryKey: ['places-lite'], enabled: open, queryFn: () => api<Paginated<PlaceLite>>('/places?pageSize=200').then((r) => r.data) });
  const users = useQuery({ queryKey: ['users-lite'], enabled: open, queryFn: () => api<Paginated<StaffUser>>('/users?pageSize=200').then((r) => r.data) });
  useEffect(() => {
    if (!open) return;
    if (!editing) return setF(defaults('SPEEDING'));
    const base = defaults(editing.type);
    setF({
      ...base,
      name: editing.name,
      severity: editing.severity,
      isEnabled: editing.isEnabled,
      allVehicles: editing.allVehicles,
      vehicleIds: editing.vehicleIds,
      groupIds: editing.groupIds,
      placeIds: editing.placeIds,
      config: { ...base.config, ...Object.fromEntries(Object.entries(editing.config).map(([k, v]) => [k, String(v)])) },
      scheduled: !!editing.schedule,
      days: editing.schedule?.days ?? base.days,
      from: editing.schedule?.from ?? base.from,
      to: editing.schedule?.to ?? base.to,
      cooldownMin: String(Math.round(editing.cooldownSec / 60)),
      recipientRoleCodes: editing.recipientRoleCodes,
      recipientUserIds: editing.recipientUserIds,
      notifyDriver: editing.notifyDriver,
      push: editing.channels.includes('PUSH'),
    });
  }, [open, editing]);
  const meta = ALERT_TYPE_CATALOG[f.type];
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name.trim(),
        type: f.type,
        severity: f.severity,
        isEnabled: f.isEnabled,
        allVehicles: meta.usesVehicles ? f.allVehicles : true,
        vehicleIds: meta.usesVehicles && !f.allVehicles ? f.vehicleIds : [],
        groupIds: meta.usesVehicles && !f.allVehicles ? f.groupIds : [],
        placeIds: meta.usesPlaces ? f.placeIds : [],
        config: Object.fromEntries(meta.config.map((c) => [c.key, c.type === 'boolean' ? f.config[c.key] === 'true' : Number(f.config[c.key])])),
        schedule: f.scheduled ? { days: f.days, from: f.from, to: f.to } : null,
        cooldownSec: Math.max(0, Math.round(Number(f.cooldownMin) * 60)),
        recipientRoleCodes: f.recipientRoleCodes,
        recipientUserIds: f.recipientUserIds,
        channels: f.push ? ['IN_APP', 'PUSH'] : ['IN_APP'],
        notifyDriver: f.notifyDriver,
      };
      return editing ? api(`/alert-rules/${editing.id}`, { method: 'PATCH', json: body }) : api('/alert-rules', { method: 'POST', json: body });
    },
    onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['alert-rules'] }); onClose(); },
    onError: toast.error,
  });
  const toggleIn = (arr: string[], v: string) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const valid = f.name.trim().length >= 2 && (!f.scheduled || f.days.length > 0) && (f.allVehicles || !meta.usesVehicles || f.vehicleIds.length + f.groupIds.length > 0);

  return (
    <Dialog open={open} onClose={onClose} size="lg" title={editing ? t('alerts.editRule') : t('alerts.addRule')} footer={<><button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button><button className="btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : null} {t('common.save')}</button></>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('alerts.type')}>{(id) => (
          <select id={id} className="input" value={f.type} disabled={!!editing} onChange={(e) => { const type = e.target.value as AlertRuleType; setF((p) => ({ ...defaults(type), name: p.name, recipientRoleCodes: p.recipientRoleCodes, recipientUserIds: p.recipientUserIds, push: p.push })); }}>
            {GROUPS.map((g) => <optgroup key={g} label={t(`alerts.groups.${g}`)}>{ALERT_RULE_TYPES.filter((x) => ALERT_TYPE_CATALOG[x].group === g).map((x) => <option key={x} value={x}>{t(`alerts.types.${x}`)}</option>)}</optgroup>)}
          </select>
        )}</Field>
        <Field label={t('alerts.ruleName')} required>{(id) => <input id={id} className="input" value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} placeholder={t(`alerts.types.${f.type}`)} />}</Field>
        <p className="text-xs text-text-muted sm:col-span-2">{t(`alerts.typeHints.${f.type}`)}</p>
        <Field label={t('alerts.severity')}>{(id) => <select id={id} className="input" value={f.severity} onChange={(e) => setF((p) => ({ ...p, severity: e.target.value as Form['severity'] }))}>{AlertSeverity.map((s) => <option key={s} value={s}>{t(`alerts.severities.${s}`)}</option>)}</select>}</Field>
        <Field label={`${t('alerts.cooldown')} (${t('units.min')})`} hint={t('alerts.cooldownHint')}>{(id) => <input id={id} type="number" dir="ltr" className="input" value={f.cooldownMin} onChange={(e) => setF((p) => ({ ...p, cooldownMin: e.target.value }))} />}</Field>
        {meta.config.map((c) => (
          <Field key={c.key} label={`${t(`alerts.config.${c.key}`)}${c.unit ? ` (${t(`units.${c.unit}`)})` : ''}`}>{(id) => c.type === 'boolean' ? <input id={id} type="checkbox" checked={f.config[c.key] === 'true'} onChange={(e) => setF((p) => ({ ...p, config: { ...p.config, [c.key]: String(e.target.checked) } }))} /> : <input id={id} type="number" dir="ltr" className="input" min={c.min} max={c.max} value={f.config[c.key] ?? ''} onChange={(e) => setF((p) => ({ ...p, config: { ...p.config, [c.key]: e.target.value } }))} />}</Field>
        ))}

        {meta.usesVehicles ? (
          <div className="sm:col-span-2">
            <span className="label">{t('alerts.scope')}</span>
            <div className="flex flex-wrap gap-3 text-sm">
              <label className="flex items-center gap-2"><input type="radio" checked={f.allVehicles} onChange={() => setF((p) => ({ ...p, allVehicles: true }))} /> {t('alerts.allVehicles')}</label>
              <label className="flex items-center gap-2"><input type="radio" checked={!f.allVehicles} onChange={() => setF((p) => ({ ...p, allVehicles: false }))} /> {t('alerts.selectedVehicles')}</label>
            </div>
            {!f.allVehicles ? (
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <ChipPicker label={t('vehicles.groups')} items={(groups.data ?? []).map((g) => ({ id: g.id, label: g.name }))} value={f.groupIds} onToggle={(id) => setF((p) => ({ ...p, groupIds: toggleIn(p.groupIds, id) }))} />
                <ChipPicker label={t('nav.vehicles')} items={(vehicles.data ?? []).map((v) => ({ id: v.id, label: v.plateAr }))} value={f.vehicleIds} onToggle={(id) => setF((p) => ({ ...p, vehicleIds: toggleIn(p.vehicleIds, id) }))} />
              </div>
            ) : null}
          </div>
        ) : null}
        {meta.usesPlaces ? <div className="sm:col-span-2"><ChipPicker label={t('alerts.placesScope')} hint={t('alerts.placesScopeHint')} items={(places.data ?? []).map((p) => ({ id: p.id, label: p.name }))} value={f.placeIds} onToggle={(id) => setF((p) => ({ ...p, placeIds: toggleIn(p.placeIds, id) }))} /></div> : null}

        <div className="sm:col-span-2">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.scheduled} onChange={(e) => setF((p) => ({ ...p, scheduled: e.target.checked }))} /> {t('alerts.scheduleOnly')}</label>
          {f.scheduled ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {[0, 1, 2, 3, 4, 5, 6].map((d) => <button key={d} type="button" className={clsx('pill border', f.days.includes(d) ? 'border-brand-ink bg-brand-soft text-brand-ink' : 'border-border text-text-muted')} onClick={() => setF((p) => ({ ...p, days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d].sort() }))}>{t(`days.${d}`)}</button>)}
              <input type="time" dir="ltr" className="input max-w-[120px]" value={f.from} onChange={(e) => setF((p) => ({ ...p, from: e.target.value }))} aria-label={t('settings.company.from')} />
              <span className="text-text-faint">→</span>
              <input type="time" dir="ltr" className="input max-w-[120px]" value={f.to} onChange={(e) => setF((p) => ({ ...p, to: e.target.value }))} aria-label={t('settings.company.to')} />
            </div>
          ) : null}
        </div>

        <div className="sm:col-span-2">
          <span className="label">{t('alerts.recipients')}</span>
          <div className="flex flex-wrap gap-2">{SYSTEM_ROLE_CODES.filter((c) => c !== 'DRIVER').map((c) => <button key={c} type="button" className={clsx('pill border', f.recipientRoleCodes.includes(c) ? 'border-brand-ink bg-brand-soft text-brand-ink' : 'border-border text-text-muted')} onClick={() => setF((p) => ({ ...p, recipientRoleCodes: toggleIn(p.recipientRoleCodes, c) }))}>{i18n.language === 'en' ? ROLE_PRESETS[c].nameEn : ROLE_PRESETS[c].nameAr}</button>)}</div>
          <div className="mt-2"><ChipPicker label={t('alerts.specificUsers')} items={(users.data ?? []).map((u) => ({ id: u.id, label: u.fullName }))} value={f.recipientUserIds} onToggle={(id) => setF((p) => ({ ...p, recipientUserIds: toggleIn(p.recipientUserIds, id) }))} /></div>
          <div className="mt-2 flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.push} onChange={(e) => setF((p) => ({ ...p, push: e.target.checked }))} /> {t('alerts.pushChannel')}</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.notifyDriver} onChange={(e) => setF((p) => ({ ...p, notifyDriver: e.target.checked }))} /> {t('alerts.notifyDriver')}</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.isEnabled} onChange={(e) => setF((p) => ({ ...p, isEnabled: e.target.checked }))} /> {t('alerts.enabled')}</label>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function ChipPicker({ label, hint, items, value, onToggle }: { label: string; hint?: string; items: { id: string; label: string }[]; value: string[]; onToggle: (id: string) => void }) {
  const [q, setQ] = useState('');
  const shown = items.filter((i) => !q || i.label.includes(q) || value.includes(i.id)).slice(0, 60);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between"><span className="label !mb-0">{label}</span><span className="text-xs text-text-faint">{value.length}</span></div>
      {hint ? <p className="mb-1 text-xs text-text-faint">{hint}</p> : null}
      {items.length > 12 ? <input className="input mb-2 !min-h-8" value={q} onChange={(e) => setQ(e.target.value)} aria-label={label} /> : null}
      <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
        {shown.map((i) => <button key={i.id} type="button" className={clsx('pill border', value.includes(i.id) ? 'border-brand-ink bg-brand-soft text-brand-ink' : 'border-border text-text-muted')} onClick={() => onToggle(i.id)}>{i.label}</button>)}
        {!shown.length ? <span className="text-xs text-text-faint">—</span> : null}
      </div>
    </div>
  );
}
